import axios from 'axios'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useFeature } from '@/composables/useFeature'
import esgAnnexC from '@/config/esgAnnexC.json'
import { DETAIL_ESG } from '@/constants'
import { useDetailStore } from './useDetailStore'
import { useEvaluationStore } from './useEvaluationStore'

// Pull a GeoJSON Polygon's exterior ring back into the [[x, y], ...] shape the
// ESG backend already accepts for parcel geometry. Interior rings (holes) are
// not relevant for "where does the building sit?" so we drop them. Returns
// null when the input isn't usable so the caller can decide whether to send
// the building_geometry parameter at all.
function geoJsonPolygonToXyRing(geoJson) {
  if (!geoJson || typeof geoJson !== 'object') return null
  if (geoJson.type !== 'Polygon') return null
  const exterior = geoJson.coordinates?.[0]
  if (!Array.isArray(exterior) || exterior.length < 3) return null

  const ring = []
  for (const point of exterior) {
    if (!Array.isArray(point) || point.length < 2) return null
    const x = Number(point[0])
    const y = Number(point[1])
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null
    ring.push([x, y])
  }
  return ring
}

const getBaseEsgSummaryStructure = () => [
  {
    factor: 'Rohemärgis',
    description: 'BREEAM<br>LEED',
    evaluation: { type: 'select', options: ['Jah', 'Ei'], value: 'Ei' },
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '1. Rohemärgis' },
  },
  {
    id: 'energiaklass',
    factor: 'Energiamärgis',
    description: 'A, B, C, D, E, F, G, H',
    evaluation: '', // Populated from ehrGeneral
    impact: '',
    reference: 'Peatükk 3',
    esg: {
      group: '2. Energiamärgis jm energia kasutuse ja tootmisega seonduv',
    },
  },
  {
    id: 'etaKek',
    factor:
      'Energiatõhususarv (ETA) või kaalutud energiaerikasutus (KEK) köetava pinna kohta, kWh/(m²·a)',
    description: 'Lisaks periood, mille kohta on andmed esitatud',
    evaluation: '', // Populated from ehrGeneral
    impact: '',
    reference: 'Peatükk 3',
    esg: {
      group: '2. Energiamärgis jm energia kasutuse ja tootmisega seonduv',
    },
  },
  {
    id: 'near_zero_energy_building',
    factor: 'Vastavus liginullenergia hoone nõuetele',
    description: 'Jah/ei',
    evaluation: { type: 'select', options: ['Jah', 'Ei'], value: '' }, // Calculated
    impact: '',
    reference: '-',
    esg: {
      group: '2. Energiamärgis jm energia kasutuse ja tootmisega seonduv',
    },
  },
  {
    id: 'energy_class_compliance',
    factor:
      'Hoone energiaklassi vastavus uutele/peatselt jõustuvatele miinimumnõuetele',
    description: 'Jah/ei',
    evaluation: { type: 'select', options: ['Jah', 'Ei'], value: '' }, // Calculated
    impact: '',
    reference: '-',
    esg: {
      group: '2. Energiamärgis jm energia kasutuse ja tootmisega seonduv',
    },
  },
  {
    factor: 'Tarnitud energia (kWh aastas)',
    description: ` - kokku<br> - sh keskküte ja -jahutus; asumisel tõhusa kaugkütte- süsteemi piirkonnas esitatakse tarnija<br> - sh võrguelekter; sh taastuvenergia paketiga võrguelekter<br> - sh kütused; sh taastuvad kütused`,
    evaluation: '', // Populated from ehrGeneral
    impact: '',
    reference: '-',
    esg: {
      group: '2. Energiamärgis jm energia kasutuse ja tootmisega seonduv',
    },
  },
  {
    factor: 'Soojusallika liigid',
    description: `(1) Soojusallika liigid on:<br>  1) katel;<br>  1.1) kaugküte;<br>  1.2) tõhus kaugküte;<br>  2) soojuspump;<br>  2.1) soojaveeboiler;<br>  3) päikesekollektor;<br>  4) ahi, kamin või pliit;<br>  4.1) koostootmisseade;<br>  5) elektriotseküte;<br>  5.1) heitvee soojustagasti;<br>  6) muu soojusallikas.`,
    evaluation: '', // Populated from ehrCommunal
    impact: '',
    reference: 'Peatükk 3',
    esg: {
      group: '2. Energiamärgis jm energia kasutuse ja tootmisega seonduv',
    },
  },
  {
    id: 'renewable_energy_production',
    factor: 'Lokaalse taastuvenergia tootmine liigiti',
    description: ` - tuuleenergia (jah/ei)<br> - hüdroenergia (jah/ei)<br> - päikeseenergia (jah/ei)<br> - biomassienergia (jah/ei)<br> - biokütus (jah/ei)<br> - soojuspumbaenergia (maasoojus, õhk-õhk, õhk-vesi) (jah/ei)`,
    evaluation: 'Avalikes registrites andmed puuduvad',
    impact: '',
    reference: 'Peatükk 3',
    esg: {
      group: '2. Energiamärgis jm energia kasutuse ja tootmisega seonduv',
    },
  },
  {
    id: 'industrial_noise_level',
    factor: 'Tööstusmüra tase aasta keskmisena (dB)',
    description: '',
    evaluation: '',
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '3. Müra' },
  },
  {
    id: 'noise_level',
    factor: 'Liiklusmüra tase aasta keskmisena (dB)',
    description: '',
    evaluation: '',
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '3. Müra' },
  },
  {
    factor:
      'Reostus ja reostuseoht, erinevad seda tõendavad materjalid, sh keskkonnaekspertiisid',
    description: 'Jah/ei',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: 'Ei',
    },
    impact: '',
    reference: '-',
    esg: { group: '4. Reostus, reostusoht, jääkreostus' },
  },
  {
    id: 'residual_pollution', // Match this with API key
    factor:
      'Jääkreostus, erinevad seda tõendavad materjalid, sh keskkonnaekspertiisid',
    description: 'Jah/ei',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: '',
    }, // Populated from fetched data
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '4. Reostus, reostusoht, jääkreostus' },
  },
  {
    id: 'hazardous_installations', // Match this with API key
    factor: 'Ohtlikud käitised ',
    description: 'Jah/ei',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: '',
    }, // Populated from fetched data
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '4. Reostus, reostusoht, jääkreostus' },
  },
  {
    id: 'flood_risk', // Match this with API key
    factor: 'Veega seonduvad riskid',
    description: ` - hoone kahjustus tugevate sademete tõttu;<br> - äkiliselt suurenenud vee läbivool jõgedes ja järvedes;<br> - merevee taseme tõus;<br> - madal mereäärne ala; ohustatud tugevate merepoolsete tuulte puhul;<br> - kas vara asub või ei asu üleujutusala riskipiirkonnas;<br> - esinemistõenäosus üks kord 10 aasta jooksul — jah/ ei;<br> - esinemistõenäosus üks kord 50 aasta jooksul — jah/ ei;<br> - esinemistõenäosus üks kord 100 aasta jooksul — jah/ ei;<br> - esinemistõenäosus üks kord 1000 aasta jooksul — jah/ ei;<br> - üleujutusalade kaardi väljavõte`,
    evaluation: {
      type: 'select',
      options: [
        'Jah, tõenäosusega 1x10 aasta jooksul',
        'Jah, tõenäosusega 1x50 aasta jooksul',
        'Jah, tõenäosusega 1x100 aasta jooksul',
        'Jah, tõenäosusega 1x1000 aasta jooksul',
        'Ei',
        'Avalikes registrites andmed puuduvad',
      ],
      value: '',
    },
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '5. Füüsilised kliimariskid ja kohanemisvajadus' },
  },
  {
    factor:
      'Probleemid veevarustusega (põhjavee taseme kriitiline langus lokaalses veehaarde piirkonnas, veestress, jms)',
    description: 'Jah/ei',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: 'Avalikes registrites andmed puuduvad',
    },
    impact: '',
    reference: '-',
    esg: { group: '5. Füüsilised kliimariskid ja kohanemisvajadus' },
  },
  {
    factor:
      'Veeriski maandavate investeeringute ja meetmete vajadus ja asjakohasus',
    description: 'Jah/ei',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: 'Avalikes registrites andmed puuduvad',
    },
    impact: '',
    reference: '-',
    esg: { group: '5. Füüsilised kliimariskid ja kohanemisvajadus' },
  },
  {
    id: 'heat_islands', // Match this with API key
    factor: 'Soojussaare olemasolu',
    description: 'Jah/ei',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: '',
    },
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '5. Füüsilised kliimariskid ja kohanemisvajadus' },
  },
  {
    factor:
      'Sundventilatsiooni ja jahutussüsteemi adekvaatsus või vajadus neid tehnosüsteeme välja/ümber ehitada (ajendiks kohanemine tõusva keskmise õhutemperatuuriga ja selle kõikuvuse kasvava amplituudi ning esinemise sagedusega)',
    description: '',
    evaluation: 'Hindaja ei ole teostanud ehitustehnilisi uuringuid',
    impact: '',
    reference: '-',
    esg: { group: '5. Füüsilised kliimariskid ja kohanemisvajadus' },
  },
  {
    factor: 'Hoone tormikindlus',
    description: '',
    evaluation: 'Hindaja ei ole teostanud ehitustehnilisi uuringuid',
    impact: '',
    reference: '-',
    esg: { group: '5. Füüsilised kliimariskid ja kohanemisvajadus' },
  },
  {
    id: 'radon_risk', // Match this with API key
    factor: 'Radoonioht',
    description: 'Jah/ei',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: '',
    }, // Populated from fetched data
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '6. Keskkonnaalased tegurid' },
  },
  {
    factor: 'Looduskaitseala koosseisu kuulumine ja muud maakasutuse piirangud',
    description: 'Jah/ei',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: '',
    },
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '6. Keskkonnaalased tegurid' },
  },
  {
    id: 'mineral_deposit', // Match this with API key
    factor: 'Maardlad',
    description: 'Jah/ei',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: '',
    }, // Populated from fetched data
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '6. Keskkonnaalased tegurid' },
  },
  {
    factor: 'Muud looduslikud ohud ja ohuallikad',
    description: 'Jah/ ei, täpsem kirjeldus',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: '',
    },
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '6. Keskkonnaalased tegurid' },
  },
  {
    factor: 'Vee päritolu',
    description: `ühisveevärk<br>puurkaev<br>salvkaev<br>muu (ennekõike pinnaveekogud)`,
    evaluation: '', // Populated from ehrCommunal
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '7. Heaolu ja keskkonnasäästu lisanäitajad' },
  },
  {
    factor: 'Reovee käitlemine',
    description: `ühiskanalisatsioon<br>kinnistul asuv omapuhasti või tööstusreoveepuhasti<br>kogumismahuti`,
    evaluation: '', // Populated from ehrCommunal
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '7. Heaolu ja keskkonnasäästu lisanäitajad' },
  },
  {
    factor: 'Nn targa maja kontseptsiooni olemasolu',
    description: 'Jah/ei',
    evaluation: { type: 'select', options: ['Jah', 'Ei'], value: '' },
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '7. Heaolu ja keskkonnasäästu lisanäitajad' },
  },
  {
    factor:
      'Elektriautode laadimiskohtade olemasolu ja kaugus lähematest laadismispunktidest',
    description: 'Jah/ei',
    evaluation: { type: 'select', options: ['Jah', 'Ei'], value: '' },
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '7. Heaolu ja keskkonnasäästu lisanäitajad' },
  },
  {
    factor: 'Jalg- ja tõukerataste parkla olemasolu',
    description: 'Jah/ei',
    evaluation: { type: 'select', options: ['Jah', 'Ei'], value: '' },
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '7. Heaolu ja keskkonnasäästu lisanäitajad' },
  },
  {
    factor: 'Insolatsioon',
    description: '-',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: '',
    },
    impact: '',
    reference: '-',
    esg: { group: '7. Heaolu ja keskkonnasäästu lisanäitajad' },
  },
  {
    factor: 'Jäätmete liigiti kogumise võimalus',
    description: 'Jah/ei',
    evaluation: { type: 'select', options: ['Jah', 'Ei'], value: '' },
    impact: '',
    reference: '-',
    esg: { group: '7. Heaolu ja keskkonnasäästu lisanäitajad' },
  },
  {
    factor: 'Hoone ligipääsetavus',
    description: 'Jah/ei',
    evaluation: { type: 'select', options: ['Jah', 'Ei'], value: '' },
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '7. Heaolu ja keskkonnasäästu lisanäitajad' },
  },
  {
    factor: 'Ühistranspordi lähedus, valikuvõimalus ja kasutusmugavus',
    description: 'Ülevaatlik kirjeldus',
    evaluation: '', // Populated from detailStore.infra
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '7. Heaolu ja keskkonnasäästu lisanäitajad' },
  },
  {
    factor: 'Hoone vastavus nõuetele',
    description:
      'kirjeldada lühidalt puudusi ja vastuolusid lubade, planeeringute jms osas ',
    evaluation: '',
    impact: '',
    reference: 'Peatükk 2',
    esg: { group: '8. Seisukord ja juhtimine' },
  },
  {
    factor:
      'Hoone vee- ja energiakasutuse monitoorimise võimalused kasutajate kaupa',
    description: '',
    evaluation: '',
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '8. Seisukord ja juhtimine' },
  },
  {
    factor: 'Hoone kaasaegsus ning kapitalikulude vajadus',
    description: '',
    evaluation: '',
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '8. Seisukord ja juhtimine' },
  },
  {
    factor:
      'Puudujäägid/ hoone tehniliste parameetrite mittevastavus uute/ peatselt jõustuvate miinimumnõuetega nt. energiaklassi mittevastavus alumisele piirmäärale',
    description: 'kommenteerida kui märgist ei ole või ei vasta piirmäärale',
    evaluation: '',
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '8. Seisukord ja juhtimine' },
  },
  {
    id: 'korteruhistu',
    factor:
      'Hinnang kinnisvara juhtimise kohta (korterelamutes ühistu olemasolu ja selle tõhusus, ettevõtete puhul juhtimisstandardite olemasolu jms)',
    description: '',
    evaluation: 'KÜ nimi',
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '8. Seisukord ja juhtimine' },
  },
  {
    factor: 'Hinnang omanikult saadud andmete kvaliteedi kohta  ',
    description:
      'esitada ilmnenud vastuolud nende olemasolul ja anda üldine hinnang andmete kvaliteedile',
    evaluation: '',
    impact: '',
    reference: 'Peatükk 3',
    esg: { group: '8. Seisukord ja juhtimine' },
  },
  {
    factor:
      'Kliimariskide analüüsi sisaldavate dokumentide olemasolu ja ülevaade kliimariskide kohanemiseks tehtud tegevustest',
    description: 'Jah/ei',
    evaluation: {
      type: 'select',
      options: ['Jah', 'Ei', 'Avalikes registrites andmed puuduvad'],
      value: '',
    },
    impact: '',
    reference: '-',
    esg: { group: '9. Taksonoomia' },
  },
]

// RED-355: the EVS 875-13:2024 annex C form, shared with the PHP export
// (App\Support\EsgAnnexC) so the factor wording, the T/Ä/E / hoonestamata maa
// / korter markers and the footnotes exist once.
export const ESG_ANNEX_C = esgAnnexC
export const ESG_NO_DATA = esgAnnexC.noData

// The five-column table's no-data answer; annex C says „info puudub“.
const LEGACY_NO_DATA = 'Avalikes registrites andmed puuduvad'

// Each five-column row's annex C factor, by position (the order
// getBaseEsgSummaryStructure() returns and saved summaries were stored in).
const legacyRows = () =>
  getBaseEsgSummaryStructure().map((row, index) => ({
    ...row,
    key: esgAnnexC.legacyOrder[index],
  }))

// Where an auto-filled value comes from — the annex C „Andmeallikas“ cell
// (footnote ***: the source must be named). Only set when the value really
// came from that source.
const EHR_SOURCE = 'ehitisregister'
const AUTO_SOURCES = {
  energiamargis: EHR_SOURCE,
  energiatohususarv: EHR_SOURCE,
  liginullenergia: EHR_SOURCE,
  tarnitud_energia: EHR_SOURCE,
  soojusenergia_allikas: EHR_SOURCE,
  taastuvenergia_tootmine: EHR_SOURCE,
  vee_paritolu: EHR_SOURCE,
  reovesi: EHR_SOURCE,
  toostusmura: 'Maa- ja Ruumiameti mürakaart',
  liiklusmura: 'Maa- ja Ruumiameti mürakaart',
  jaakreostus: 'Keskkonnaagentuuri jääkreostuse kaart (EELIS)',
  ohtlikud_kaitised: 'Maa- ja Ruumiameti ohtlike käitiste kaart',
  uleujutusrisk: 'Maa- ja Ruumiameti üleujutusalade kaart',
  soojussaared: 'Maa- ja Ruumiameti soojussaarte kaart',
  radoonioht: 'Eesti Geoloogiateenistuse radooniriski kaart',
  maardlad: 'Maa- ja Ruumiameti maardlate kaart',
  yhistransport: 'ühistranspordi peatuste andmed',
  juhtimine: 'äriregister',
}

// The five-column table's impact cell offered these phrases; on restore the
// phrase becomes the annex C verdict and the rest stays the mitigation
// (App\Support\EsgAnnexC::verdictFromLegacyText applies the same rule).
const LEGACY_IMPACT_PHRASES = [
  ['Mõju vara väärtusele ei ole täheldatav', 'mõju puudub'],
  ['Mõju väärtusele on positiivne', 'positiivne'],
  ['Mõju väärtusele on negatiivne', 'negatiivne'],
  ['Mõju väärtusele puudub', 'mõju puudub'],
]

export const verdictFromLegacyText = text => {
  const value = typeof text === 'string' ? text : ''
  for (const [phrase, verdict] of LEGACY_IMPACT_PHRASES) {
    if (value.toLowerCase().startsWith(phrase.toLowerCase())) {
      return [verdict, value.slice(phrase.length).replace(/^[\s.,;]+|[\s.,;]+$/g, '')]
    }
  }
  return ['', value]
}

// A five-column answer moves onto its annex C factor only when a valuer gave
// it. Auto-derived ones do not: old row 4 („vastavus“) was filled from the
// near-zero-energy flag — inverted onto annex C's „mittevastavus“ it would
// claim „Jah“ for every other building — and a hard-coded default nobody
// changed („Ei“, „Hindaja ei ole teostanud …“, „KÜ nimi“) is no answer. What
// cannot be told apart from an auto value is treated as one; the factor then
// keeps its fresh lookup or reads „info puudub“. App\Support\EsgAnnexC
// applies the same rule.
const legacyAnswer = (key, saved) => {
  const raw = saved !== null && typeof saved === 'object' ? saved.value : saved
  const value = typeof raw === 'string' ? raw.trim() : ''
  if (
    esgAnnexC.legacyAutoOnly.includes(key) ||
    esgAnnexC.legacyNotAnswers.includes(value) ||
    (esgAnnexC.legacyDefaultAnswers[key] ?? []).includes(value)
  ) {
    return ''
  }
  return value
}

const isSelect = evaluation =>
  evaluation !== null && typeof evaluation === 'object' && 'value' in evaluation

/** The indicator as text, whether the row is a select or a free-text cell. */
export const esgEvaluationText = row =>
  (isSelect(row?.evaluation) ? row.evaluation.value : row?.evaluation) ?? ''

const yesNoSelect = () => ({
  type: 'select',
  options: ['Jah', 'Ei', ESG_NO_DATA],
  value: '',
})

// Annex C rows: the form's 49 factors. A factor the five-column table had
// keeps its auto-fill id, input type and reference; every hard-coded default
// answer („Ei“, „Avalikes registrites andmed puuduvad“, „Hindaja ei ole
// teostanud …“) starts empty instead — an answer must come from data or the
// valuer, and an empty one prints „info puudub“ (EVS 875-13 §5.1.5, §7.2.2).
const annexCRows = () => {
  const legacyByKey = Object.fromEntries(legacyRows().map(row => [row.key, row]))

  return esgAnnexC.factors.map(factor => {
    const legacy = legacyByKey[factor.key]
    let evaluation = ''
    if (isSelect(legacy?.evaluation)) {
      // Keep the row's own answers (the flood row's 1x10 … 1x1000 years),
      // with the annex C no-data answer in place of the old one.
      const options = legacy.evaluation.options.map(option =>
        option === LEGACY_NO_DATA ? ESG_NO_DATA : option,
      )
      evaluation = {
        type: 'select',
        options: options.includes(ESG_NO_DATA) ? options : [...options, ESG_NO_DATA],
        value: '',
      }
    } else if (!legacy && factor.input === 'yesno') {
      evaluation = yesNoSelect()
    }

    return {
      ...(legacy?.id ? { id: legacy.id } : {}),
      key: factor.key,
      factor: factor.factor,
      subgroup: factor.subgroup,
      use: factor.use,
      land: factor.land,
      apartment: factor.apartment,
      description: factor.description,
      suggestedSource: factor.suggestedSource,
      source: '',
      evaluation,
      impactVerdict: '',
      impact: '',
      reference: legacy?.reference ?? '-',
      esg: { group: factor.group },
    }
  })
}

/** The annex C rows with nothing filled — what a report says with no data at all. */
export const emptyAnnexCRows = () => annexCRows()

export const useEsgStore = defineStore('esg', () => {
  const loading = ref(false)

  const evaluationStore = useEvaluationStore()

  // Factors to fetch data for (keys match API endpoints and esgData keys)
  const esgFactors = [
    'noise_level',
    'traffic_volume',
    'mineral_deposit',
    'hazardous_installations',
    'residual_pollution',
    'radon_risk',
    'flood_risk',
    'heat_islands',
    'industrial_noise_level',
    'nearest_main_road_distance',
  ]

  const buildInitialEsgData = () =>
    Object.fromEntries(esgFactors.map(key => [key, {
      fetched: { loading: true, found: false, data: null, error: null },
      user: { status: false, description: '', evaluation: '' },
    }]))

  const buildInitialLegends = () =>
    Object.fromEntries(Object.keys(DETAIL_ESG).map(key => [key, []]))

  // Holds fetched data AND user inputs for the individual EsgItem components
  // Initialized inline (not in onMounted) so data is available regardless of tab visibility
  const esgData = ref(buildInitialEsgData())

  // Holds the data for the EsgTableComponent, initialized and then editable
  const esgSummary = ref([])

  // RED-355: the summary is the EVS 875-13 annex C form while the flag is on.
  const annexCEnabled = useFeature('esg-annex-c')

  // The annex C rows to show or export: the built summary, or — before any
  // ESG lookup ran, or after a reset — the whole form with every factor
  // „info puudub“, never an empty table.
  const annexCSummary = computed(() =>
    esgSummary.value.length && esgSummary.value[0]?.use !== undefined
      ? esgSummary.value
      : emptyAnnexCRows(),
  )

  // The Kiirhindamine view of the form: what the lookups found and where
  // from. No valuer weighs the impact and the report has no chapters to point
  // at, so impact is „info puudub“ and the reference „-“. The result page
  // shows these rows and the lühiaruanne prints them, so the two never differ.
  const quickValuationAnnexCRows = computed(() =>
    annexCSummary.value.map(row => ({
      ...row,
      impactVerdict: '',
      impact: '',
      reference: '-',
    })),
  )

  const korteruhistu = ref('')
  // Bumped by every lookup and every reset; a lookup answers only while it is the latest.
  let korteruhistuRequest = 0

  const legends = ref(buildInitialLegends())

  // Pinia outlives an Inertia visit — without this, /evaluation/13 →
  // /evaluation/14 keeps 13's ESG state. korteruhistu is the worst leak: only
  // the EHR (building) flow ever writes it, so a land parcel visited after a
  // building would show the previous building's korteriühistu row.
  const resetForEvaluation = () => {
    // A korteriühistu lookup still in flight belongs to the evaluation being
    // left: its answer must not land on the next one.
    korteruhistuRequest++
    loading.value = false
    esgData.value = buildInitialEsgData()
    esgSummary.value = []
    korteruhistu.value = ''
    legends.value = buildInitialLegends()
  }

  // Helper to get initial evaluation value for table based on fetched status
  const getInitialEvaluationForTable = (factorId, fetchedData) => {
    const defaultNoData = 'Avalikes registrites andmed puuduvad'
    if (!fetchedData || fetchedData.error) return defaultNoData

    // For factors where evaluation is directly Jah/Ei based on 'found'
    const simpleFactors = [
      'hazardous_installations',
      'heat_islands',
      'traffic_volume',
    ]

    if (simpleFactors.includes(factorId)) {
      return fetchedData.found ? 'Jah' : 'Ei'
    }

    if (factorId === 'mineral_deposit') {
      if (!fetchedData.found) {
        return defaultNoData
      }
      return fetchedData.data.found ? 'Jah' : 'Ei'
    }

    if (factorId === 'residual_pollution') {
      if (!fetchedData.found) {
        return defaultNoData
      }
      return fetchedData.data.present ? 'Jah' : 'Ei'
    }

    // Specific logic for radon
    if (factorId === 'radon_risk') {
      if (!fetchedData.found || !fetchedData.data?.kov?.level) {
        return defaultNoData // Or 'Ei' if preferred when not found
      }

      return 'Jah'
    }

    if (factorId === 'flood_risk') {
      if (!fetchedData.found || !fetchedData.data?.probability) {
        return 'Ei'
      }
      return `Jah, tõenäosusega 1x${fetchedData.data.probability} aasta jooksul`
    }

    if (factorId === 'industrial_noise_level') {
      if (!fetchedData.found || !fetchedData.data?.level) {
        return defaultNoData
      }
      return `${fetchedData.data.level} dB`
    }

    if (factorId === 'noise_level') {
      if (!fetchedData.found || !fetchedData.data?.level) {
        return defaultNoData
      }
      return `${fetchedData.data.level} dB`
    }

    // If factor doesn't match specific logic, return default
    return defaultNoData
  }

  const generateEsgItemEvaluation = (key, fetchedData) => {
    if (!fetchedData) {
      return ''
    }

    if (!fetchedData.found) {
      switch (key) {
        case 'flood_risk':
          return ''
        case 'mineral_deposit':
          return ''
        default:
          return ''
      }
    }
  }

  // Helper to generate default description for EsgItem
  const generateEsgItemDescription = (key, fetchedData) => {
    if (!fetchedData) {
      return ''
    }

    if (!fetchedData.found) {
      switch (key) {
        case 'noise_level':
          return `Maa- ja Ruumiameti mürakaardi kaardirakenduse andmetel jääb hinnatav vara väljapoole mõõdetud müratasemeid.`
        case 'flood_risk':
          return `Maa- ja Ruumiameti üleujutusalade kaardirakenduse andmetel ei paikne hinnatav vara üleujutusriskiga maa-alal.`
        case 'mineral_deposit':
          return `Maa- ja Ruumiameti maardlate kaardirakenduse andmetel ei paikne hinnatav vara teadaolevalt maardlate (s.h. prognoosvarude) maa-alal ega ka altkaevandatud maa-alal.`
        case 'hazardous_installations':
          return `Maa- ja Ruumiameti ohtlike käitiste kaardirakenduse andmetel ei jää hinnatav vara ohtliku käitise ohualasse.`
        case 'heat_islands':
          return `Maa- ja Ruumiameti soojussaarte kaardirakenduse andmetel ei paikne hinnatav vara soojussaarte mõjualas.`
        case 'residual_pollution':
          return `Keskkonnaportaali kaardirakenduse andmetel ei ole hinnatava vara suhtes teada võimalikku jääkreostust.`
        case 'traffic_volume':
          return `Maa- ja Ruumiameti teeregistri kaardirakenduse andmetel ei ole hinnatava vara lähiümbruse täpne liiklussagedus teada.`
        default:
          return 'Avalikes registrites andmed puuduvad'
      }
    }

    if (!fetchedData.data) {
      return ''
    }

    const data = fetchedData.data // API response structure might differ slightly

    switch (key) {
      case 'noise_level':
        return `Maa- ja Ruumiameti mürakaardi kaardirakenduse andmetel jääb hinnatav vara liiklusmüra mõjualasse, kus keskmine ööpäevane müra on ${
          data.level || 'teadmata'
        } dB.`
      case 'traffic_volume':
        return `Teeregistri andmetel on lähim tee, mille kohta on olemas liiklussageduse andmed, liiklussagedusega ${
          data.level || 'teadmata'
        }.`
      case 'mineral_deposit':
        return data.found
          ? `Maa- ja Ruumiameti maardlate kaardirakenduse andmetel jääb hinnatav vara maardla alale.`
          : 'Maa- ja Ruumiameti maardlate kaardirakenduse andmetel ei paikne hinnatav vara teadaolevalt maardlate (s.h. prognoosvarude) maa-alal ega ka altkaevandatud maa-alal.'
      case 'hazardous_installations':
        return `Maa- ja Ruumiameti ohtlike käitiste kaardirakenduse andmetel jääb hinnatav vara ohtliku käitise ohualasse (käitise nimi ${data.name}).`
      case 'residual_pollution':
        return data.present
          ? `Keskkonnaportaali kaardirakenduse andmetel jääb hinnatav vara jääkreostuse alale (jääkreostuse nimi: ${data.name}, registrikood ${data.id})`
          : 'Keskkonnaportaali kaardirakenduse andmetel ei ole hinnatava vara suhtes teada võimalikku jääkreostust.'
      case 'radon_risk': {
        // Ensure data.kov and data.kov.label exist
        const kovLabel = data.kov?.label || 'tundmatu tasemega'
        return data.kov?.level !== 2
          ? `Eesti pinnase radooniriski kaardirakenduse andmetel paikneb hinnatav vara alal, kus võib esineda ${kovLabel} radoonirisk.`
          : `Eesti pinnase radooniriski kaardirakenduse andmetel paikneb hinnatav vara alal, kus esineb täiendav radooni uuringuvajadus ehk täpne info puudub.`
      }
      case 'flood_risk':
        return `Maa- ja Ruumiameti üleujutusalade kaardirakenduse andmetel paikneb hinnatav vara üleujutusriskiga maa-alal, kus üleujutuse esinemise tõenäosus on 1 x ${data.probability} aasta jooksul.`
      case 'heat_islands':
        return `Maa- ja Ruumiameti soojussaarte kaardirakenduse andmetel jääb hinnatav vara soojussaare alale, kus soojustaseme lävendiks on ${data.threshold} kraadi.`
      default:
        console.warn(`No description generation logic for ESG item: ${key}`)
        return ''
    }
  }

  // Action to fetch all ESG data. The first argument is named `bbox` for
  // historical reasons but is actually the parcel polygon (an array of [x, y]
  // pairs in EPSG:3301). We forward it as `geometry` so the backend can use
  // it as an OGC Intersects filter — the bbox envelope is kept alongside only
  // because the road-distance endpoints still expand it to a radius search.
  //
  // `buildingGeometry` is the optional EHR building footprint as a GeoJSON
  // Polygon (already produced server-side via `EhrBuildingDataService` and
  // surfaced on `data.xRoadEhr.geometry[0]`). When supplied, the backend
  // prefers it over the parcel polygon for polygon-intersect WFS queries so
  // the export matches what the map shows *at the building's pixels*, not at
  // the parcel centroid.
  const fetchAllEsgData = async (bbox, buildingGeometry = null) => {
    if (!bbox || !Array.isArray(bbox) || bbox.length === 0) {
      console.error('Invalid polygon provided for fetching ESG data:', bbox)
      return
    }

    loading.value = true

    let minX = bbox[0][0],
      minY = bbox[0][1],
      maxX = bbox[0][0],
      maxY = bbox[0][1]
    for (let i = 1; i < bbox.length; i++) {
      minX = Math.min(minX, bbox[i][0])
      minY = Math.min(minY, bbox[i][1])
      maxX = Math.max(maxX, bbox[i][0])
      maxY = Math.max(maxY, bbox[i][1])
    }
    const params = {
      minX,
      minY,
      maxX,
      maxY,
      geometry: JSON.stringify(bbox),
    }

    const buildingRing = geoJsonPolygonToXyRing(buildingGeometry)
    if (buildingRing) {
      params.building_geometry = JSON.stringify(buildingRing)
    }

    // Initialize state structure
    esgFactors.forEach(key => {
      esgData.value[key] = {
        fetched: { loading: true, found: false, data: null, error: null },
        user: { status: false, description: '', evaluation: '' }, // Initialize user data
      }
    })

    // Create fetch promises
    /*esgFactors.forEach((key) => {
      const endpoint = `/api/esg/${key}` // Replace underscores for API endpoint
      const promise = axios
        .get(endpoint, { params })
        .then((response) => {
          esgData.value[key].fetched = {
            loading: false,
            found: response.data?.found || false,
            data: response.data?.data || null, // Adjust based on actual API response
            error: response.data?.found ? null : response.data?.error,
          }
        })
        .catch((error) => {
          console.error(`Error fetching ESG data for ${key}:`, error)
          esgData.value[key].fetched = {
            loading: false,
            found: false,
            data: null,
            error: error.response?.data?.error || error.message || 'Fetch error',
          }
        })
      promises.push(promise)
    }) */

    await axios
      .get('/api/esg/all', { params })
      .then(response => {
        Object.entries(response.data.data).forEach(([key, value]) => {
          esgData.value[key].fetched = {
            loading: false,
            found: value.found || false,
            data: value.data || null, // Adjust based on actual API response
            error: value.found ? null : value.error,
          }
        })
      })
      .catch(error => {
        console.error('Error fetching all ESG data:', error)
      })

    // Wait for all fetches to complete
    // await Promise.allSettled(promises)

    // Initialize user data based on fetched results and initialize summary table
    initializeEsgData()
    initializeEsgSummary()

    loading.value = false
  }

  const fetchKorteruhistu = async query => {
    const request = ++korteruhistuRequest
    try {
      const response = await axios.get('/api/esg/korteruhistu', {
        params: { query: query },
      })
      if (request !== korteruhistuRequest) return
      korteruhistu.value = response.data['korteruhistu'] || ''
    } catch (error) {
      console.error('Korteriühistu lookup failed:', error)
    }
  }

  // The RIK korteriühistu lookup and the /api/poi/closest transit stops
  // resolve on their own schedules — often after initializeEsgSummary() has
  // taken its one-shot snapshot, or after restoreFromSaved() has overlaid a
  // previously saved empty value. Late fills only ever land on an empty row,
  // so text the user wrote (or a restored non-empty edit) always wins.
  const fillSummaryRowIfEmpty = (key, value) => {
    if (!value) return
    const row = esgSummary.value.find(row => row.key === key)
    if (row && !row.evaluation) {
      row.evaluation = value
      if ('source' in row && !row.source) {
        row.source = AUTO_SOURCES[key] ?? ''
      }
    }
  }

  const applyLateAutoFills = () => {
    fillSummaryRowIfEmpty('juhtimine', korteruhistu.value)
    fillSummaryRowIfEmpty('yhistransport', useDetailStore().infra?.yhistransport)
  }

  watch(korteruhistu, applyLateAutoFills)

  // Registered lazily: useDetailStore() cannot run during this store's setup
  // (the two stores instantiate each other).
  let infraWatchStarted = false
  const watchInfraForLateFill = () => {
    if (infraWatchStarted) return
    infraWatchStarted = true
    const { infra } = storeToRefs(useDetailStore())
    watch(() => infra.value?.yhistransport, applyLateAutoFills)
  }

  // Action to initialize user data based on fetched data
  const initializeEsgData = () => {
    esgFactors.forEach(key => {
      const fetchedResult = esgData.value[key]?.fetched
      if (fetchedResult) {
        // Set initial status active
        esgData.value[key].user.status = true // fetchedResult.found

        // Set initial description if user hasn't entered one yet
        if (!esgData.value[key].user.description) {
          esgData.value[key].user.description = generateEsgItemDescription(
            key,
            fetchedResult,
          )
        }

        if (!esgData.value[key].user.evaluation) {
          esgData.value[key].user.evaluation = generateEsgItemEvaluation(
            key,
            fetchedResult,
          )
        }
        // Initialize evaluation based on fetched data (if applicable, most are text)
        // This might need more specific logic depending on the factor
      }
    })
  }

  // Action to initialize the summary table data. Rows are matched by their
  // annex C `key` (the five-column rows carry one too), so one fill serves
  // both forms.
  const initializeEsgSummary = () => {
    watchInfraForLateFill()
    const detailStore = useDetailStore() // Get instance inside action
    const { ehrGeneral, ehrCommunal, infra } = storeToRefs(detailStore) // Make refs reactive

    const annexC = annexCEnabled.value
    const baseStructure = annexC ? annexCRows() : legacyRows()

    esgSummary.value = baseStructure.map(item => {
      const newItem = { ...item } // Clone base item

      // Populate from EHR/Infra data
      if (item.key === 'energiamargis') {
        newItem.evaluation = ehrGeneral.value['energiaklass'] || '-'
      } else if (item.key === 'energiatohususarv') {
        if (ehrGeneral.value['energiaklass-tohusustyyp'] === '-') {
          newItem.evaluation = LEGACY_NO_DATA
        } else {
          newItem.evaluation = `${ehrGeneral.value['energiaklass-tohusustyyp']} ${ehrGeneral.value['energiaklass-tohususarv']} kWh/m²•a; märgis väljastatud ${ehrGeneral.value['energiaklass-aasta']}`
        }
      } else if (
        item.key === 'liginullenergia' ||
        // The five-column row asked „vastavus“; annex C asks „mittevastavus“,
        // which the near-zero-energy answer does not tell — left to the valuer.
        (item.key === 'energiaklassi_mittevastavus' && !annexC)
      ) {
        newItem.evaluation.value = ehrGeneral.value['liginullenergiahoone']
      } else if (item.key === 'tarnitud_energia') {
        newItem.evaluation =
          ehrGeneral.value['energiaklass-tarnitud-energia'] || LEGACY_NO_DATA
      } else if (item.key === 'soojusenergia_allikas') {
        newItem.evaluation =
          ehrCommunal.value['soojusvarustuse_liik'] || LEGACY_NO_DATA
      } else if (item.key === 'taastuvenergia_tootmine') {
        newItem.evaluation =
          ehrGeneral.value['energiaklass-taastuvenergia'] || (annexC ? '' : 'Ei')
      } else if (item.key === 'vee_paritolu') {
        newItem.evaluation =
          ehrCommunal.value['veevarustuse_liik'] || LEGACY_NO_DATA
      } else if (item.key === 'reovesi') {
        newItem.evaluation =
          ehrCommunal.value['kanalisatsiooni_liik'] || LEGACY_NO_DATA
      } else if (item.key === 'yhistransport') {
        newItem.evaluation = infra.value?.yhistransport || '' // Assuming infra is reactive ref
      } else if (item.key === 'juhtimine') {
        newItem.evaluation = korteruhistu.value
      }

      // Populate 'evaluation' for items linked to fetched ESG data via 'id'.
      // Annex C: a lookup that never answered (request failed, so `loading`
      // is still set) or answered with an error is no data — not „Ei“.
      const fetchedData = item.id ? esgData.value[item.id]?.fetched : null
      const unanswered = annexC && (!fetchedData || fetchedData.loading || fetchedData.error)
      if (item.id && esgData.value[item.id] && !unanswered) {
        if (
          newItem.evaluation &&
          typeof newItem.evaluation === 'object' &&
          'value' in newItem.evaluation
        ) {
          // Handle cases where evaluation is an object (like select dropdowns)
          newItem.evaluation.value = getInitialEvaluationForTable(
            item.id,
            fetchedData,
          )
        } else if (typeof newItem.evaluation === 'string') {
          // Override 'Kvalitatiivne' with Jah/Ei/etc. based on fetched data
          newItem.evaluation = getInitialEvaluationForTable(
            item.id,
            fetchedData,
          )
        }
      }

      return annexC ? toAnnexCAnswer(newItem) : newItem
    })
  }

  // Annex C: a lookup that found nothing leaves the cell empty — it prints
  // „info puudub“ — and a value that came from a register names it as the
  // source (footnote ***).
  const toAnnexCAnswer = row => {
    const value = esgEvaluationText(row)
    const empty = value === '' || value === '-' || value === LEGACY_NO_DATA

    if (isSelect(row.evaluation)) {
      row.evaluation = { ...row.evaluation, value: empty ? '' : value }
    } else {
      row.evaluation = empty ? '' : value
    }
    row.source = empty ? '' : (AUTO_SOURCES[row.key] ?? '')

    return row
  }

  // A saved indicator into a row: a select keeps its own options and takes
  // the saved answer (a legacy „Avalikes registrites andmed puuduvad“ is the
  // annex C „info puudub“), a text cell takes the text.
  const assignEvaluation = (row, saved) => {
    const raw = isSelect(saved) ? saved.value : saved
    const text = typeof raw === 'string' ? raw : ''
    if (isSelect(row.evaluation)) {
      row.evaluation.value =
        text === LEGACY_NO_DATA && row.evaluation.options?.includes(ESG_NO_DATA)
          ? ESG_NO_DATA
          : text
    } else {
      row.evaluation = text
    }
  }

  const restoreAnnexCRow = (row, saved, legacy) => {
    if (saved.evaluation !== undefined) {
      if (!legacy) {
        assignEvaluation(row, saved.evaluation)
      } else if (legacyAnswer(row.key, saved.evaluation) !== '') {
        assignEvaluation(row, legacyAnswer(row.key, saved.evaluation))
      }
    }
    if (saved.reference !== undefined) {
      row.reference = saved.reference
    }
    if (saved.source !== undefined && 'source' in row) {
      row.source = saved.source
    }
    if (saved.impact === undefined) return

    if (legacy && saved.impactVerdict === undefined && 'impactVerdict' in row) {
      const [verdict, mitigation] = verdictFromLegacyText(saved.impact)
      row.impactVerdict = verdict
      row.impact = mitigation
      return
    }
    row.impact = saved.impact
    if (saved.impactVerdict !== undefined && 'impactVerdict' in row) {
      row.impactVerdict = saved.impactVerdict
    }
  }

  /**
   * Save ESG user edits to the evaluation via the shared debounced save mechanism.
   * Collects both card-level user inputs and summary table editable fields.
   */
  const saveEsgData = () => {
    const items = {}
    esgFactors.forEach(key => {
      if (esgData.value[key]?.user) {
        items[key] = { ...esgData.value[key].user }
      }
    })

    // Annex C rows are saved by their factor key, so adding or reordering a
    // factor never shifts a valuer's answer onto another row.
    const summary = esgSummary.value.map(row =>
      'impactVerdict' in row
        ? {
            key: row.key,
            evaluation: esgEvaluationText(row),
            source: row.source,
            impactVerdict: row.impactVerdict,
            impact: row.impact,
            reference: row.reference,
          }
        : {
            evaluation: row.evaluation,
            impact: row.impact,
            reference: row.reference,
          },
    )

    evaluationStore.save({ esg_data: { items, summary } })
  }

  /**
   * Restore ESG user edits from saved data (loaded from database).
   * Called after ESG data is fetched and auto-initialized, to overlay user's previous edits.
   */
  const restoreFromSaved = (savedData) => {
    if (!savedData) return

    // Restore card-level user inputs
    if (savedData.items) {
      Object.entries(savedData.items).forEach(([key, userData]) => {
        if (esgData.value[key]?.user) {
          Object.assign(esgData.value[key].user, userData)
        }
      })
    }

    // Restore summary table editable fields. Rows saved by the annex C table
    // carry their factor key; rows saved by the five-column table do not and
    // are matched by position — onto an annex C table through the old row
    // order, so an evaluation saved before the flag keeps every answer.
    const savedRows = Array.isArray(savedData.summary) ? savedData.summary : []
    const keyed = savedRows.some(saved => typeof saved?.key === 'string')
    const annexTable = esgSummary.value.some(row => 'impactVerdict' in row)

    if (keyed || (annexTable && savedRows.length === esgAnnexC.legacyOrder.length)) {
      savedRows.forEach((saved, index) => {
        const key = keyed ? saved?.key : esgAnnexC.legacyOrder[index]
        const row = esgSummary.value.find(row => row.key === key)
        if (row && saved) {
          restoreAnnexCRow(row, saved, !keyed)
        }
      })
    } else if (savedData.summary && Array.isArray(savedData.summary) && esgSummary.value.length === savedData.summary.length) {
      savedData.summary.forEach((saved, index) => {
        if (saved.evaluation !== undefined) {
          esgSummary.value[index].evaluation = saved.evaluation
        }
        if (saved.impact !== undefined) {
          esgSummary.value[index].impact = saved.impact
        }
        if (saved.reference !== undefined) {
          esgSummary.value[index].reference = saved.reference
        }
      })
    }

    // A previously saved empty auto-fill row must not shadow a source value
    // that has already resolved.
    applyLateAutoFills()
  }

  return {
    loading,
    esgData, // Provides { fetched: {...}, user: {...} } structure
    esgSummary, // Provides data array for the table
    annexCEnabled,
    annexCSummary,
    quickValuationAnnexCRows,
    legends,
    fetchAllEsgData,
    fetchKorteruhistu,
    resetForEvaluation,
    initializeEsgData, // Kept separate if needed later, called by fetchAll
    initializeEsgSummary, // Kept separate if needed later, called by fetchAll
    saveEsgData,
    restoreFromSaved,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useEsgStore, import.meta.hot))
}
